import { useState } from "react";
import { faqItems } from "@/mocks/faq";
import Breadcrumb from "@/components/feature/Breadcrumb";
import type { BreadcrumbItem } from "@/components/feature/Breadcrumb";

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: "首页", path: "/" },
    { label: "关于我们" },
  ];

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "土豪漫画网",
    url: window.location.origin,
    description:
      "土豪漫画网 - 韩漫在线免费阅读平台，提供韩漫全集下拉式阅读，收录韩国漫画大全。",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "contact@tuhaomanhua.com",
      availableLanguage: ["Chinese"],
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "关于我们 - 土豪漫画网",
    description:
      "了解土豪漫画网——韩漫在线免费阅读平台，提供韩漫全集下拉式阅读，收录韩国漫画大全。",
    url: `${window.location.origin}/about`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      <main className="pt-20 pb-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Brand Intro */}
          <section className="mb-14">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900">
                关于土豪漫画网
              </h1>
              <p className="mt-4 text-foreground-600 leading-relaxed">
                土豪漫画网是一个专注于<strong>韩漫在线免费</strong>阅读的平台，致力于为漫画爱好者提供优质的<strong>韩国漫画大全</strong>资源。
                我们支持<strong>韩漫全集下拉式阅读</strong>方式，让用户无需下载任何应用，即可在网页上流畅浏览各种韩漫作品，
                享受沉浸式的阅读体验。
              </p>
            </div>
          </section>

          {/* Features */}
          <section className="mb-14">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: "ri-book-open-line",
                  title: "海量资源",
                  desc: "收录丰富的韩国漫画资源，涵盖热血、恋爱、奇幻、玄幻、悬疑等多种类型，满足不同读者的口味需求。",
                },
                {
                  icon: "ri-smartphone-line",
                  title: "下拉式阅读",
                  desc: "采用韩国Webtoon主流的滚轮下拉式阅读方式，专为网页和手机优化，无需翻页，阅读体验流畅自然。",
                },
                {
                  icon: "ri-currency-line",
                  title: "完全免费",
                  desc: "站内所有漫画内容均可免费阅读，无需付费订阅，让每一位漫友都能轻松畅享韩漫的魅力。",
                },
                {
                  icon: "ri-refresh-line",
                  title: "同步更新",
                  desc: "与韩国平台保持同步更新，热门作品每周准时更新最新章节，让读者不会错过任何精彩内容。",
                },
                {
                  icon: "ri-device-line",
                  title: "多端适配",
                  desc: "采用响应式设计，完美适配电脑、平板和手机等多种设备，随时随地都能享受阅读乐趣。",
                },
                {
                  icon: "ri-shield-check-line",
                  title: "品质保障",
                  desc: "精选高质量漫画资源，高清画质呈现，确保每一位读者都能获得最佳的视觉体验。",
                },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className="bg-background-100 rounded-xl p-6 hover:bg-background-200/50 transition-colors duration-200"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 mb-4">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation Stats */}
          <section className="mb-14 bg-background-100 rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { value: "500万+", label: "月活跃用户" },
                { value: "2000+", label: "收录漫画" },
                { value: "98%", label: "用户好评率" },
                { value: "5年", label: "运营时间" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500">
                    {stat.value}
                  </div>
                  <div className="mt-2 text-sm text-foreground-500">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-14">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqItems.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-background-100 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-semibold text-foreground-800 pr-4">
                      {item.question}
                    </span>
                    <div
                      className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-200 ${
                        openFaq === idx
                          ? "bg-primary-500 text-background-50 rotate-45"
                          : "bg-background-200 text-foreground-500"
                      }`}
                    >
                      <i className="ri-add-line text-sm"></i>
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === idx ? "max-h-96" : "max-h-0"
                    }`}
                  >
                    <p className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              联系我们
            </h2>
            <div className="bg-background-100 rounded-2xl p-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 mb-3">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">
                    电子邮箱
                  </h3>
                  <p className="text-sm text-foreground-500">
                    contact@tuhaomanhua.com
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 mb-3">
                    <i className="ri-wechat-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">
                    微信公众号
                  </h3>
                  <p className="text-sm text-foreground-500">土豪漫画网</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 mb-3">
                    <i className="ri-timer-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">
                    工作时间
                  </h3>
                  <p className="text-sm text-foreground-500">
                    周一至周五 9:00-18:00
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}